import { useState, useEffect, useCallback, useRef } from 'react';
import { Form } from 'antd';
import { message } from 'antd';
import { systemSettingsService, MealAllowanceConfig as ServiceMealAllowanceConfig } from '@/services/system-settings.service';
import { handleApiError } from '@/utils/errorHandler';
import { fetchImageAsDataUri } from '@/contexts/AvatarContext';

// Types
export interface CompanyInfo {
  companyName: string;
  address: string;
  taxCode: string;
  phoneNumber: string;
}

export interface WorkShift {
  id: string;
  name: string;
  enabled: boolean;
  checkInTime?: string;
  checkOutTime?: string;
  lunchBreakStart?: string;
  lunchBreakEnd?: string;
  standardWorkingHours?: number;
  applyToAllEmployees?: boolean;
  applicableUserIds?: number[];
  applicableDepartmentIds?: number[];
}

export interface MealAllowanceConfig {
  minimumHours: number;
}

export interface Employee {
  id: string | number;
  name: string;
  employeeCode: string;
  departmentId: string | number;
  avatar?: string | null;
}

export interface DepartmentWithEmployees {
  id: string | number;
  name: string;
  employees: Employee[];
}

export interface GPSPoint {
  id: string;
  locationName: string;
  address: string;
  latitude: string;
  longitude: string;
  radius: number;
  allowOutsideRadius: boolean;
  applicableScope: string;
  selectedEmployeeIds?: string[];
  enabled: boolean;
}

const emptyCompanyInfo: CompanyInfo = {
  companyName: '',
  address: '',
  taxCode: '',
  phoneNumber: '',
};

const defaultMealAllowance: MealAllowanceConfig = {
  minimumHours: 4,
};

export interface UseSystemSettingsResult {
  // Form instance
  form: ReturnType<typeof Form.useForm>[0];

  // Loading states
  loading: boolean;
  initialLoading: boolean;
  loadingDepartments: boolean;
  error: string | null;

  // Company Info
  companyInfo: CompanyInfo;
  initialCompanyInfo: CompanyInfo;
  companyInfoChanged: boolean;
  isNewCompany: boolean;
  hasCompanyInteracted: boolean;
  setHasCompanyInteracted: (value: boolean) => void;

  // Work Shifts
  workShifts: WorkShift[];
  initialWorkShifts: WorkShift[];
  workShiftsChanged: boolean;
  expandedShifts: Set<string>;
  shiftErrors: Record<string, Record<string, string>>;

  // Meal Allowance
  mealAllowance: MealAllowanceConfig;
  initialMealAllowance: MealAllowanceConfig;
  mealAllowanceChanged: boolean;
  hasUserInteracted: boolean;
  setHasUserInteracted: (value: boolean) => void;

  // GPS Points
  gpsPoints: GPSPoint[];
  initialGpsPoints: GPSPoint[];
  gpsPointsChanged: boolean;
  expandedGpsPoints: Set<string>;

  // Expanded sections
  expandedSections: Set<string>;

  // Applicable modal (for selecting employees)
  applicableModalVisible: string | null;
  searchText: string;
  setSearchText: (value: string) => void;
  selectedEmployees: Record<string, string[]>;
  departmentsWithEmployees: DepartmentWithEmployees[];
  expandedDepartments: Set<string>;

  // Avatar cache
  avatarCacheRef: React.MutableRefObject<Map<string, string>>;
  fetchAvatarAsDataUri: (imageUrl: string) => Promise<string | null>;
  avatarRefreshKey: number;

  // Actions - Company Info
  handleCompanyInfoSave: () => Promise<void>;
  handleCompanyInfoCancel: () => void;

  // Actions - Work Shifts
  toggleShift: (shiftId: string) => void;
  handleShiftToggle: (shiftId: string, enabled: boolean) => Promise<void>;
  handleShiftTimeChange: (shiftId: string, field: string, value: string | undefined) => void;
  handleStandardHoursChange: (shiftId: string, value: number | null) => void;
  updateWorkShift: (shiftId: string, updates: Partial<WorkShift>) => void;
  addWorkShift: () => void;
  handleWorkShiftsSubmit: () => Promise<void>;
  handleWorkShiftsCancel: () => void;

  // Actions - Meal Allowance
  handleMealAllowanceSave: () => Promise<void>;
  handleMealAllowanceCancel: () => void;

  // Actions - GPS Points
  toggleGpsPoint: (pointId: string) => void;
  handleGpsPointToggle: (pointId: string, enabled: boolean) => Promise<void>;
  addGpsPoint: () => void;
  updateGpsPoint: (pointId: string, field: keyof GPSPoint, value: any) => void;
  handleGpsConfigSave: () => Promise<void>;
  handleGpsConfigCancel: () => void;

  // Actions - Sections
  toggleSection: (sectionId: string) => void;

  // Actions - Applicable Modal
  openApplicableModal: (key: string, selectedIds?: string[]) => void;
  closeApplicableModal: () => void;
  toggleDepartment: (deptId: string) => void;
  handleEmployeeToggle: (key: string, employeeId: string) => void;
  handleDepartmentToggle: (key: string, department: DepartmentWithEmployees) => void;
  handleSelectAll: (key: string) => void;
  handleSaveApplicableSelection: (key: string) => void;
  getFilteredDepartments: () => DepartmentWithEmployees[];
  getApplicableScopeDisplay: (point: GPSPoint) => string;
  getWorkShiftApplicableDisplay: (shift: WorkShift) => string;
}

export const useSystemSettings = (): UseSystemSettingsResult => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Company Info
  const [companyInfo, setCompanyInfo] = useState<CompanyInfo>(emptyCompanyInfo);
  const [initialCompanyInfo, setInitialCompanyInfo] = useState<CompanyInfo>(emptyCompanyInfo);
  const [companyInfoChanged, setCompanyInfoChanged] = useState(false);
  const [isNewCompany, setIsNewCompany] = useState(true);
  const [hasCompanyInteracted, setHasCompanyInteracted] = useState(false);

  // Work Shifts
  const [workShifts, setWorkShifts] = useState<WorkShift[]>([]);
  const [initialWorkShifts, setInitialWorkShifts] = useState<WorkShift[]>([]);
  const [workShiftsChanged, setWorkShiftsChanged] = useState(false);
  const [expandedShifts, setExpandedShifts] = useState<Set<string>>(new Set());
  const [shiftErrors, setShiftErrors] = useState<Record<string, Record<string, string>>>({});

  // Meal Allowance
  const [initialMealAllowance, setInitialMealAllowance] = useState<MealAllowanceConfig>(defaultMealAllowance);
  const [mealAllowanceChanged, setMealAllowanceChanged] = useState(false);
  const [hasUserInteracted, setHasUserInteracted] = useState(false);

  // GPS Points
  const [gpsPoints, setGpsPoints] = useState<GPSPoint[]>([]);
  const [initialGpsPoints, setInitialGpsPoints] = useState<GPSPoint[]>([]);
  const [gpsPointsChanged, setGpsPointsChanged] = useState(false);
  const [expandedGpsPoints, setExpandedGpsPoints] = useState<Set<string>>(new Set());

  // Expanded sections
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());

  // Applicable modal
  const [applicableModalVisible, setApplicableModalVisible] = useState<string | null>(null);
  const [searchText, setSearchText] = useState('');
  const [selectedEmployees, setSelectedEmployees] = useState<Record<string, string[]>>({});
  const [departmentsWithEmployees, setDepartmentsWithEmployees] = useState<DepartmentWithEmployees[]>([]);
  const [loadingDepartments, setLoadingDepartments] = useState(false);
  const [expandedDepartments, setExpandedDepartments] = useState<Set<string>>(new Set());

  // Avatar cache
  const avatarCacheRef = useRef<Map<string, string>>(new Map());
  const [avatarRefreshKey, setAvatarRefreshKey] = useState(0);

  // Helper function để fetch avatar với cache
  // imageUrl ở đây đã là full URL (được build bằng buildFullUrl trước đó)
  const fetchAvatarAsDataUri = useCallback(async (imageUrl: string): Promise<string | null> => {
    // Check cache first
    if (avatarCacheRef.current.has(imageUrl)) {
      return avatarCacheRef.current.get(imageUrl) || null;
    }

    try {
      // Fetch avatar trực tiếp từ URL và convert sang data URI
      const dataUri = await fetchImageAsDataUri(imageUrl);

      // Cache the result
      if (dataUri) {
        avatarCacheRef.current.set(imageUrl, dataUri);
      }

      return dataUri;
    } catch (err) {
      console.error('Error loading avatar:', err);
      return null;
    }
  }, []);

  // Listen for avatar-updated event để clear cache và refresh avatar khi có user upload avatar mới
  useEffect(() => {
    const handleAvatarUpdated = async (event: CustomEvent<{ userId: string }>) => {
      const userId = event.detail.userId;
      
      // Clear avatar cache cho user đó (tìm tất cả cache entries có chứa userId trong URL)
      const keysToDelete: string[] = [];
      avatarCacheRef.current.forEach((_, key) => {
        if (key.includes(userId) || key.includes(`/avatars/`)) {
          keysToDelete.push(key);
        }
      });
      keysToDelete.forEach(key => avatarCacheRef.current.delete(key));
      
      // Increment refresh key để force re-render avatar components
      setAvatarRefreshKey(prev => prev + 1);
      
      // Nếu modal đang mở, refresh lại departments để lấy avatar mới từ server
      if (applicableModalVisible) {
        try {
          const data = await systemSettingsService.getDepartmentsWithEmployees(searchText || undefined);
          const formattedData: DepartmentWithEmployees[] = data.map((dept) => ({
            id: String(dept.id),
            name: dept.name,
            employees: dept.employees.map((emp) => ({
              id: String(emp.id),
              name: emp.name,
              employeeCode: emp.employeeCode,
              departmentId: String(emp.departmentId),
              avatar: emp.avatar || null,
            })),
          }));
          setDepartmentsWithEmployees(formattedData);
        } catch (error) {
          console.error('Error refreshing departments after avatar update:', error);
        }
      }
    };

    const eventHandler = handleAvatarUpdated as unknown as EventListener;
    window.addEventListener('avatar-updated', eventHandler);
    return () => {
      window.removeEventListener('avatar-updated', eventHandler);
    };
  }, [applicableModalVisible, searchText]);

  // Normalize time: chấp nhận "08:00", "08 : 00", và backend "08:00:00" -> normalize to "08:00"
  const normalizeTime = useCallback((time: string | undefined): string | undefined => {
    if (!time) return undefined;
    const trimmed = time.trim();
    if (!trimmed) return undefined;
    const normalized = trimmed.replace(/\s*:\s*/g, ':');
    const parts = normalized.split(':');
    if (parts.length < 2) return undefined;
    const hh = parts[0]?.trim();
    const mm = parts[1]?.trim();
    if (!hh || !mm) return undefined;
    return `${hh.padStart(2, '0')}:${mm.padStart(2, '0')}`;
  }, []);

  // Calculate standard working hours: (checkOut - checkIn) - (lunchBreakEnd - lunchBreakStart)
  const calculateStandardHours = useCallback((
    checkInTime?: string,
    checkOutTime?: string,
    lunchBreakStart?: string,
    lunchBreakEnd?: string
  ): number | undefined => {
    const checkIn = normalizeTime(checkInTime);
    const checkOut = normalizeTime(checkOutTime);
    
    if (!checkIn || !checkOut) {
      return undefined;
    }

    // Parse times to minutes
    const parseTimeToMinutes = (time: string): number => {
      const [hours, minutes] = time.split(':').map(Number);
      return hours * 60 + minutes;
    };

    try {
      const checkInMinutes = parseTimeToMinutes(checkIn);
      const checkOutMinutes = parseTimeToMinutes(checkOut);
      
      // Handle case where check-out is next day (e.g., night shift)
      let totalMinutes = checkOutMinutes - checkInMinutes;
      if (totalMinutes < 0) {
        totalMinutes += 24 * 60; // Add 24 hours
      }

      // Subtract lunch break if both start and end are provided
      const lunchStart = normalizeTime(lunchBreakStart);
      const lunchEnd = normalizeTime(lunchBreakEnd);
      
      if (lunchStart && lunchEnd) {
        const lunchStartMinutes = parseTimeToMinutes(lunchStart);
        const lunchEndMinutes = parseTimeToMinutes(lunchEnd);
        let lunchBreakMinutes = lunchEndMinutes - lunchStartMinutes;
        
        // Handle case where lunch break spans midnight (unlikely but possible)
        if (lunchBreakMinutes < 0) {
          lunchBreakMinutes += 24 * 60;
        }
        
        totalMinutes -= lunchBreakMinutes;
      }

      // Convert to hours (with 2 decimal places)
      const hours = totalMinutes / 60;
      return Math.round(hours * 100) / 100; // Round to 2 decimal places
    } catch (error) {
      return undefined;
    }
  }, [normalizeTime]);

  // Validate work shift: kiểm tra tên, giờ vào, giờ ra, giờ nghỉ trưa
  const validateShift = useCallback((shift: WorkShift): Record<string, string> => {
    const errors: Record<string, string> = {};
    const timeRegex = /^\d{2}:\d{2}$/;

    if (!shift.name || !shift.name.trim()) {
      errors.name = 'Tên ca là bắt buộc';
    }
    const checkIn = normalizeTime(shift.checkInTime);
    if (!checkIn || !timeRegex.test(checkIn)) {
      errors.checkInTime = 'Giờ vào phải có format HH:mm (VD: 07:00)';
    }
    const checkOut = normalizeTime(shift.checkOutTime);
    if (!checkOut || !timeRegex.test(checkOut)) {
      errors.checkOutTime = 'Giờ ra phải có format HH:mm (VD: 17:00)';
    }
    if (checkIn && checkOut && timeRegex.test(checkIn) && timeRegex.test(checkOut)) {
      if (checkIn === checkOut) {
        errors.checkOutTime = 'Giờ ra phải khác giờ vào';
      }
    }
    const lunchStart = normalizeTime(shift.lunchBreakStart);
    if (shift.lunchBreakStart && (!lunchStart || !timeRegex.test(lunchStart))) {
      errors.lunchBreakStart = 'Giờ bắt đầu nghỉ trưa phải có format HH:mm';
    }
    const lunchEnd = normalizeTime(shift.lunchBreakEnd);
    if (shift.lunchBreakEnd && (!lunchEnd || !timeRegex.test(lunchEnd))) {
      errors.lunchBreakEnd = 'Giờ kết thúc nghỉ trưa phải có format HH:mm';
    }
    return errors;
  }, [normalizeTime]);

  // Load initial data: company info, work shifts, GPS points, meal allowance
  useEffect(() => {
    let cancelled = false; // Flag để cancel nếu component unmount

    const loadData = async () => {
      try {
        setInitialLoading(true);
        setError(null);
        
        // Load company info
        const companyInfoData = await systemSettingsService.getCompanyInfo();
        if (!cancelled) {
          if (companyInfoData) {
            setInitialCompanyInfo(companyInfoData);
            setCompanyInfo(companyInfoData);
            form.setFieldsValue({
              companyInfo: companyInfoData,
            });
            setIsNewCompany(false);
          } else {
            // Chưa có company info, để trống
            setInitialCompanyInfo(emptyCompanyInfo);
            setCompanyInfo(emptyCompanyInfo);
            form.setFieldsValue({
              companyInfo: emptyCompanyInfo,
            });
            setIsNewCompany(true);
          }
          setCompanyInfoChanged(false);
        }
        
        // Load work shifts
        const shifts = await systemSettingsService.getWorkShifts();
        if (!cancelled) {
          // Calculate standard working hours for each shift if not already calculated
          const shiftsWithCalculatedHours = shifts.map((shift) => {
            if (shift.checkInTime && shift.checkOutTime) {
              const calculatedHours = calculateStandardHours(
                shift.checkInTime,
                shift.checkOutTime,
                shift.lunchBreakStart,
                shift.lunchBreakEnd
              );
              // Only update if calculated hours is valid and different from existing
              if (calculatedHours !== undefined) {
                return { ...shift, standardWorkingHours: calculatedHours };
              }
            }
            return shift;
          });
          setWorkShifts(shiftsWithCalculatedHours);
          setInitialWorkShifts(JSON.parse(JSON.stringify(shiftsWithCalculatedHours))); // Deep copy
          setWorkShiftsChanged(false);
        }
        
        // Load GPS locations
        const locations = await systemSettingsService.getGpsLocations();
        if (!cancelled) {
          setGpsPoints(locations);
          setInitialGpsPoints(JSON.parse(JSON.stringify(locations)));
          setGpsPointsChanged(false);
        }
        
        // Load meal allowance config
        const mealConfig = await systemSettingsService.getMealAllowanceConfig();
        if (!cancelled) {
          setInitialMealAllowance(mealConfig);
          form.setFieldsValue({
            mealAllowance: mealConfig,
          });
        }
      } catch (error: any) {
        if (!cancelled) {
          const errorMessage = error?.message || 'Không thể tải dữ liệu cài đặt hệ thống';
          handleApiError(error, errorMessage);
          setError(errorMessage);
        }
      } finally {
        if (!cancelled) {
          setInitialLoading(false);
        }
      }
    };

    loadData();

    // Cleanup: cancel request nếu component unmount trước khi API trả về
    return () => {
      cancelled = true;
    };
  }, [form, calculateStandardHours]);

  // Helper function to normalize company info for comparison
  const normalizeCompanyInfo = useCallback((info: CompanyInfo | undefined | null): CompanyInfo => {
    if (!info) {
      return emptyCompanyInfo;
    }
    return {
      companyName: String(info.companyName || '').trim(),
      address: String(info.address || '').trim(),
      taxCode: String(info.taxCode || '').trim(),
      phoneNumber: String(info.phoneNumber || '').trim(),
    };
  }, []);

  // Watch company info changes
  const currentCompanyInfo = Form.useWatch('companyInfo', form);
  useEffect(() => {
    // Chỉ check khi user đã tương tác và không còn trong trạng thái loading initial
    if (initialLoading || !hasCompanyInteracted) {
      return;
    }

    if (!currentCompanyInfo) {
      setCompanyInfoChanged(false);
      setHasCompanyInteracted(false);
      return;
    }

    // Normalize both values before comparing to handle edge cases (empty strings, undefined, whitespace)
    const normalizedCurrent = normalizeCompanyInfo(currentCompanyInfo);
    const normalizedInitial = normalizeCompanyInfo(initialCompanyInfo);
    
    const hasChanges = JSON.stringify(normalizedCurrent) !== JSON.stringify(normalizedInitial);
    setCompanyInfoChanged(hasChanges);
    
    // Reset interaction flag if values match (user reverted to original state)
    if (!hasChanges) {
      setHasCompanyInteracted(false);
    }
  }, [currentCompanyInfo, initialCompanyInfo, initialLoading, hasCompanyInteracted, normalizeCompanyInfo]);

  // Watch meal allowance changes
  const currentMealAllowance = Form.useWatch(['mealAllowance', 'minimumHours'], form);
  useEffect(() => {
    // Only check for changes if user has interacted with the field
    if (!hasUserInteracted) {
      return;
    }
    
    if (currentMealAllowance !== undefined && currentMealAllowance !== null && currentMealAllowance !== '') {
      const current = Number(currentMealAllowance);
      const initial = Number(initialMealAllowance.minimumHours);
      setMealAllowanceChanged(current !== initial);
    } else {
      setMealAllowanceChanged(false);
    }
  }, [currentMealAllowance, initialMealAllowance.minimumHours, hasUserInteracted]);

  // Check for work shifts changes
  useEffect(() => {
    const hasChanges = JSON.stringify(workShifts) !== JSON.stringify(initialWorkShifts);
    setWorkShiftsChanged(hasChanges);
  }, [workShifts, initialWorkShifts]);

  // Check for GPS configuration changes
  useEffect(() => {
    const hasChanges = JSON.stringify(gpsPoints) !== JSON.stringify(initialGpsPoints);
    setGpsPointsChanged(hasChanges);
  }, [gpsPoints, initialGpsPoints]);

  // Fetch departments with employees when search text changes or modal opens
  useEffect(() => {
    if (!applicableModalVisible) {
      setDepartmentsWithEmployees([]);
      return;
    }

    let cancelled = false;

    const fetchDepartments = async () => {
      try {
        setLoadingDepartments(true);
        const data = await systemSettingsService.getDepartmentsWithEmployees(searchText || undefined);
        
        if (!cancelled) {
          const formattedData: DepartmentWithEmployees[] = data.map((dept) => ({
            id: String(dept.id),
            name: dept.name,
            employees: dept.employees.map((emp) => ({
              id: String(emp.id),
              name: emp.name,
              employeeCode: emp.employeeCode,
              departmentId: String(emp.departmentId),
              avatar: emp.avatar || null,
            })),
          }));
          setDepartmentsWithEmployees(formattedData);
          
          // Nếu có search text, tự động mở tất cả phòng ban có nhân viên
          if (searchText && searchText.trim()) {
            const departmentsToExpand = formattedData
              .filter((dept) => dept.employees.length > 0)
              .map((dept) => String(dept.id));
            setExpandedDepartments(new Set(departmentsToExpand));
          } else {
            // Nếu không có search, đóng tất cả
            setExpandedDepartments(new Set());
          }
        }
      } catch (error: any) {
        if (!cancelled) {
          handleApiError(error, 'Không thể tải danh sách phòng ban và nhân viên');
        }
      } finally {
        if (!cancelled) {
          setLoadingDepartments(false);
        }
      }
    };

    // If searchText is empty and modal just opened, fetch immediately
    // Otherwise, debounce search to avoid too many API calls
    if (!searchText && applicableModalVisible) {
      // Fetch immediately when modal opens with empty search
      fetchDepartments();
    } else {
      // Debounce search when user is typing (300ms)
      const timeoutId = setTimeout(() => {
        fetchDepartments();
      }, 300);

      return () => {
        cancelled = true;
        clearTimeout(timeoutId);
      };
    }

    return () => {
      cancelled = true;
    };
  }, [searchText, applicableModalVisible]);

  // Actions - Company Info
  const handleCompanyInfoSave = useCallback(async () => {
    try {
      await form.validateFields(['companyInfo']);
      const formValues = form.getFieldsValue();
      setLoading(true);
      setError(null);
      
      const updated = await systemSettingsService.saveCompanyInfo(formValues.companyInfo, isNewCompany);
      setInitialCompanyInfo(updated);
      setCompanyInfo(updated);
      form.setFieldsValue({
        companyInfo: updated,
      });
      setIsNewCompany(false);
      setCompanyInfoChanged(false);
      setHasCompanyInteracted(false);
      message.success('Lưu thông tin doanh nghiệp thành công');
    } catch (error: any) {
      const errorMessage = error?.message || 'Lưu thông tin doanh nghiệp thất bại';
      handleApiError(error, errorMessage);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [form, isNewCompany]);

  const handleCompanyInfoCancel = useCallback(() => {
    form.setFieldsValue({
      companyInfo: initialCompanyInfo,
    });
    setCompanyInfoChanged(false);
    setHasCompanyInteracted(false);
  }, [form, initialCompanyInfo]);

  // Actions - Work Shifts
  const toggleShift = useCallback((shiftId: string) => {
    setExpandedShifts((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(shiftId)) {
        newSet.delete(shiftId);
      } else {
        newSet.add(shiftId);
      }
      return newSet;
    });
  }, []);

  const handleShiftToggle = useCallback(async (shiftId: string, enabled: boolean) => {
    // Update local state immediately
    setWorkShifts((prev) =>
      prev.map((shift) => (shift.id === shiftId ? { ...shift, enabled } : shift))
    );

    // Update on server if it's an existing shift
    if (shiftId.match(/^\d+$/)) {
      try {
        if (enabled) {
          await systemSettingsService.activateWorkShift(shiftId);
        } else {
          await systemSettingsService.updateWorkShift(shiftId, { enabled });
        }
      } catch (error: any) {
        handleApiError(error, 'Cập nhật ca làm việc thất bại');
        // Revert on error
        setWorkShifts((prev) =>
          prev.map((shift) => (shift.id === shiftId ? { ...shift, enabled: !enabled } : shift))
        );
      }
    }
  }, []);

  const handleShiftTimeChange = useCallback((shiftId: string, field: string, value: string | undefined) => {
    // Chỉ normalize cho các field thời gian, không normalize cho name
    const timeFields = ['checkInTime', 'checkOutTime', 'lunchBreakStart', 'lunchBreakEnd'];
    const normalizedValue = timeFields.includes(field) ? normalizeTime(value) : value;
    setWorkShifts((prev) => {
      const updatedShifts = prev.map((shift) => {
        if (shift.id !== shiftId) return shift;
        
        const updatedShift = { ...shift, [field]: normalizedValue };
        
        // Auto-calculate standard working hours when time fields change
        if (timeFields.includes(field)) {
          const calculatedHours = calculateStandardHours(
            updatedShift.checkInTime,
            updatedShift.checkOutTime,
            updatedShift.lunchBreakStart,
            updatedShift.lunchBreakEnd
          );
          updatedShift.standardWorkingHours = calculatedHours;
        }
        
        return updatedShift;
      });
      const updatedShift = updatedShifts.find((s) => s.id === shiftId);
      if (updatedShift) {
        const errors = validateShift(updatedShift);
        setShiftErrors((prevErrors) => ({ ...prevErrors, [shiftId]: errors }));
      }
      return updatedShifts;
    });
  }, [normalizeTime, validateShift, calculateStandardHours]);

  // Allow manual editing of standard working hours
  const handleStandardHoursChange = useCallback((shiftId: string, value: number | null) => {
    setWorkShifts((prev) =>
      prev.map((shift) => (shift.id === shiftId ? { ...shift, standardWorkingHours: value || undefined } : shift))
    );
  }, []);

  const updateWorkShift = useCallback((shiftId: string, updates: Partial<WorkShift>) => {
    setWorkShifts((prev) =>
      prev.map((shift) => (shift.id === shiftId ? { ...shift, ...updates } : shift))
    );
  }, []);

  const addWorkShift = useCallback(() => {
    const newShift: WorkShift = {
      id: `new-${Date.now()}`,
      name: '',
      enabled: true,
      checkInTime: undefined,
      checkOutTime: undefined,
      lunchBreakStart: undefined,
      lunchBreakEnd: undefined,
      standardWorkingHours: undefined,
      applyToAllEmployees: true,
    };
    setWorkShifts([...workShifts, newShift]);
    setExpandedShifts((prev) => new Set([...prev, newShift.id]));
  }, [workShifts]);

  const validateAllShifts = useCallback(() => {
    const allErrors: Record<string, Record<string, string>> = {};
    workShifts.forEach((s) => {
      const e = validateShift(s);
      if (Object.keys(e).length) {
        allErrors[s.id] = e;
      }
    });
    setShiftErrors(allErrors);
    return allErrors;
  }, [workShifts, validateShift]);

  const handleWorkShiftsSubmit = useCallback(async () => {
    try {
      // Validate shifts first
      const shiftValidation = validateAllShifts();
      if (Object.keys(shiftValidation).length) {
        message.error('Vui lòng sửa lỗi cấu hình ca làm việc trước khi lưu');
        return;
      }
      setLoading(true);
      setError(null);

      // Save work shifts only - gọi song song để tăng tốc độ
      const shiftPromises = workShifts.map(async (shift) => {
        if (shift.id.startsWith('new-') || !shift.id.match(/^\d+$/)) {
          // New shift - create
          const { id, ...shiftData } = shift;
          return await systemSettingsService.createWorkShift(shiftData);
        } else {
          // Existing shift - update
          return await systemSettingsService.updateWorkShift(shift.id, shift);
        }
      });

      await Promise.all(shiftPromises);
      
      message.success('Lưu cài đặt ca làm việc thành công');
      
      // Reload data to get updated IDs
      const shifts = await systemSettingsService.getWorkShifts();
      // Calculate standard working hours for each shift
      const shiftsWithCalculatedHours = shifts.map((shift) => {
        if (shift.checkInTime && shift.checkOutTime) {
          const calculatedHours = calculateStandardHours(
            shift.checkInTime,
            shift.checkOutTime,
            shift.lunchBreakStart,
            shift.lunchBreakEnd
          );
          if (calculatedHours !== undefined) {
            return { ...shift, standardWorkingHours: calculatedHours };
          }
        }
        return shift;
      });
      setWorkShifts(shiftsWithCalculatedHours);
      setInitialWorkShifts(JSON.parse(JSON.stringify(shiftsWithCalculatedHours))); // Deep copy
      setWorkShiftsChanged(false);
    } catch (error: any) {
      const errorMessage = error?.message || 'Lưu cài đặt ca làm việc thất bại';
      handleApiError(error, errorMessage);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [workShifts, validateAllShifts, calculateStandardHours]);

  const handleWorkShiftsCancel = useCallback(() => {
    // Reset work shifts to initial state
    setWorkShifts(JSON.parse(JSON.stringify(initialWorkShifts))); // Deep copy
    setWorkShiftsChanged(false);
    setExpandedShifts(new Set());
  }, [initialWorkShifts]);

  // Actions - Meal Allowance
  const handleMealAllowanceSave = useCallback(async () => {
    try {
      await form.validateFields(['mealAllowance']);
      const formValues = form.getFieldsValue();
      setLoading(true);
      setError(null);

      // Tự quy đổi giá trị thành number trước khi gửi lên backend
      const mealAllowanceData: ServiceMealAllowanceConfig = {
        minimumHours: Number(formValues.mealAllowance.minimumHours),
        defaultMealAllowance: formValues.mealAllowance.defaultMealAllowance 
          ? Number(formValues.mealAllowance.defaultMealAllowance) 
          : undefined,
      };
      
      const updated = await systemSettingsService.updateMealAllowanceConfig(mealAllowanceData);
      
      // Update initial value after successful save
      setInitialMealAllowance(updated);
      setMealAllowanceChanged(false);
      setHasUserInteracted(false);
      message.success('Lưu cài đặt phụ cấp cơm thành công');
    } catch (error: any) {
      const errorMessage = error?.message || 'Lưu cài đặt phụ cấp cơm thất bại';
      handleApiError(error, errorMessage);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [form]);

  const handleMealAllowanceCancel = useCallback(() => {
    form.setFieldsValue({
      mealAllowance: initialMealAllowance,
    });
    setMealAllowanceChanged(false);
    setHasUserInteracted(false);
  }, [form, initialMealAllowance]);

  // Actions - GPS Points
  const toggleGpsPoint = useCallback((pointId: string) => {
    setExpandedGpsPoints((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(pointId)) {
        newSet.delete(pointId);
      } else {
        newSet.add(pointId);
      }
      return newSet;
    });
  }, []);

  const handleGpsPointToggle = useCallback(async (pointId: string, enabled: boolean) => {
    // Find point before updating state
    const point = gpsPoints.find((p) => p.id === pointId);
    
    // Update local state immediately
    setGpsPoints((prev) =>
      prev.map((p) => (p.id === pointId ? { ...p, enabled } : p))
    );

    // Update on server if it's an existing point
    if (pointId.match(/^\d+$/) && point) {
      try {
        await systemSettingsService.updateGpsLocation(pointId, { enabled });
      } catch (error: any) {
        handleApiError(error, 'Cập nhật điểm GPS thất bại');
        // Revert on error
        setGpsPoints((prev) =>
          prev.map((p) => (p.id === pointId ? { ...p, enabled: !enabled } : p))
        );
      }
    }
  }, [gpsPoints]);

  const addGpsPoint = useCallback(() => {
    const newPoint: GPSPoint = {
      id: `new-${Date.now()}`,
      locationName: '',
      address: '',
      latitude: '',
      longitude: '',
      radius: 100,
      allowOutsideRadius: false,
      applicableScope: 'all',
      selectedEmployeeIds: [],
      enabled: true,
    };
    setGpsPoints([...gpsPoints, newPoint]);
    setExpandedGpsPoints((prev) => new Set([...prev, newPoint.id]));
  }, [gpsPoints]);

  const updateGpsPoint = useCallback((pointId: string, field: keyof GPSPoint, value: any) => {
    setGpsPoints((prevPoints) =>
      prevPoints.map((point) => (point.id === pointId ? { ...point, [field]: value } : point))
    );
  }, []);

  const handleGpsConfigSave = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Gọi song song để tăng tốc độ
      const locationPromises = gpsPoints.map(async (location) => {
        if (location.id.startsWith('new-') || !location.id.match(/^\d+$/)) {
          const { id, ...locationData } = location;
          return await systemSettingsService.createGpsLocation(locationData);
        } else {
          return await systemSettingsService.updateGpsLocation(location.id, location);
        }
      });

      await Promise.all(locationPromises);

      const locations = await systemSettingsService.getGpsLocations();
      setGpsPoints(locations);
      setInitialGpsPoints(JSON.parse(JSON.stringify(locations)));
      setGpsPointsChanged(false);

      message.success('Lưu cài đặt GPS chấm công thành công');
    } catch (error: any) {
      const errorMessage = error?.message || 'Lưu cài đặt GPS chấm công thất bại';
      handleApiError(error, errorMessage);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [gpsPoints]);

  const handleGpsConfigCancel = useCallback(() => {
    setGpsPoints(JSON.parse(JSON.stringify(initialGpsPoints)));
    setGpsPointsChanged(false);
    setExpandedGpsPoints(new Set());
  }, [initialGpsPoints]);

  // Actions - Sections
  const toggleSection = useCallback((sectionId: string) => {
    setExpandedSections((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(sectionId)) {
        newSet.delete(sectionId);
      } else {
        newSet.add(sectionId);
      }
      return newSet;
    });
  }, []);

  // Actions - Applicable Modal
  const openApplicableModal = useCallback((key: string, selectedIds?: string[]) => {
    if (selectedIds) {
      setSelectedEmployees((prev) => ({
        ...prev,
        [key]: selectedIds,
      }));
    }
    setApplicableModalVisible(key);
    setSearchText(''); // Reset search when opening modal
  }, []);

  const closeApplicableModal = useCallback(() => {
    setApplicableModalVisible(null);
    setSearchText('');
    setDepartmentsWithEmployees([]);
  }, []);

  const toggleDepartment = useCallback((deptId: string) => {
    setExpandedDepartments((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(deptId)) {
        newSet.delete(deptId);
      } else {
        newSet.add(deptId);
      }
      return newSet;
    });
  }, []);

  const handleEmployeeToggle = useCallback((key: string, employeeId: string) => {
    const current = selectedEmployees[key] || [];
    const newSelection = current.includes(employeeId)
      ? current.filter((id) => id !== employeeId)
      : [...current, employeeId];
    
    setSelectedEmployees({
      ...selectedEmployees,
      [key]: newSelection,
    });
  }, [selectedEmployees]);

  const handleDepartmentToggle = useCallback((key: string, department: DepartmentWithEmployees) => {
    const current = selectedEmployees[key] || [];
    const deptEmployeeIds = department.employees.map((emp) => String(emp.id));
    const allSelected = deptEmployeeIds.every((id) => current.includes(id));
    
    const newSelection = allSelected
      ? current.filter((id) => !deptEmployeeIds.includes(id))
      : [...new Set([...current, ...deptEmployeeIds])];
    
    setSelectedEmployees({
      ...selectedEmployees,
      [key]: newSelection,
    });
  }, [selectedEmployees]);

  const handleSelectAll = useCallback((key: string) => {
    const allEmployeeIds = departmentsWithEmployees.flatMap((dept) =>
      dept.employees.map((emp) => String(emp.id))
    );
    setSelectedEmployees({
      ...selectedEmployees,
      [key]: allEmployeeIds,
    });
  }, [departmentsWithEmployees, selectedEmployees]);

  const handleSaveApplicableSelection = useCallback((key: string) => {
    const [type, rawId] = key.split(':');
    const selectedIds = selectedEmployees[key] || [];

    // Derive department ids: department is selected if all its employees are selected
    const selectedDepartmentIds = departmentsWithEmployees
      .filter((dept) => {
        const empIds = dept.employees.map((e) => String(e.id));
        return empIds.length > 0 && empIds.every((id) => selectedIds.includes(id));
      })
      .map((dept) => Number(dept.id))
      .filter((id) => !isNaN(id));

    if (type === 'gps') {
      updateGpsPoint(rawId, 'selectedEmployeeIds', selectedIds);
    }

    if (type === 'ws') {
      setWorkShifts((prev) =>
        prev.map((s) =>
          s.id === rawId
            ? {
                ...s,
                applyToAllEmployees: false,
                applicableUserIds: selectedIds.map((x) => Number(x)).filter((n) => !isNaN(n)),
                applicableDepartmentIds: selectedDepartmentIds,
              }
            : s
        )
      );
    }

    closeApplicableModal();
  }, [selectedEmployees, departmentsWithEmployees, updateGpsPoint, closeApplicableModal]);

  const getFilteredDepartments = useCallback(() => {
    // API already handles search, so just return the data
    return departmentsWithEmployees;
  }, [departmentsWithEmployees]);

  const getApplicableScopeDisplay = useCallback((point: GPSPoint) => {
    if (point.applicableScope === 'all') {
      return 'Tất cả đối tượng';
    } else if (point.applicableScope === 'specific') {
      const count = point.selectedEmployeeIds?.length || 0;
      return count > 0 ? `${count} đối tượng đã chọn` : 'Chưa chọn đối tượng';
    }
    return 'Tất cả đối tượng';
  }, []);

  const getWorkShiftApplicableDisplay = useCallback((shift: WorkShift) => {
    if (shift.applyToAllEmployees !== false) return 'Tất cả đối tượng';
    const userCount = shift.applicableUserIds?.length || 0;
    const deptCount = shift.applicableDepartmentIds?.length || 0;
    const total = userCount + deptCount;
    return total > 0 ? `${total} đối tượng đã chọn` : 'Chưa chọn đối tượng';
  }, []);

  return {
    form,
    loading,
    initialLoading,
    loadingDepartments,
    error,
    companyInfo,
    initialCompanyInfo,
    companyInfoChanged,
    isNewCompany,
    hasCompanyInteracted,
    setHasCompanyInteracted,
    workShifts,
    initialWorkShifts,
    workShiftsChanged,
    expandedShifts,
    shiftErrors,
    mealAllowance: initialMealAllowance,
    initialMealAllowance,
    mealAllowanceChanged,
    hasUserInteracted,
    setHasUserInteracted,
    gpsPoints,
    initialGpsPoints,
    gpsPointsChanged,
    expandedGpsPoints,
    expandedSections,
    applicableModalVisible,
    searchText,
    setSearchText,
    selectedEmployees,
    departmentsWithEmployees,
    expandedDepartments,
    avatarCacheRef,
    fetchAvatarAsDataUri,
    avatarRefreshKey,
    handleCompanyInfoSave,
    handleCompanyInfoCancel,
    toggleShift,
    handleShiftToggle,
    handleShiftTimeChange,
    handleStandardHoursChange,
    updateWorkShift,
    addWorkShift,
    handleWorkShiftsSubmit,
    handleWorkShiftsCancel,
    handleMealAllowanceSave,
    handleMealAllowanceCancel,
    toggleGpsPoint,
    handleGpsPointToggle,
    addGpsPoint,
    updateGpsPoint,
    handleGpsConfigSave,
    handleGpsConfigCancel,
    toggleSection,
    openApplicableModal,
    closeApplicableModal,
    toggleDepartment,
    handleEmployeeToggle,
    handleDepartmentToggle,
    handleSelectAll,
    handleSaveApplicableSelection,
    getFilteredDepartments,
    getApplicableScopeDisplay,
    getWorkShiftApplicableDisplay,
  };
};

